import {
  ApiError,
  requestJson,
  type ApiEnvelope,
} from '@shared/api/http';
import { getWebDeviceMetadata } from '@shared/utils/device';
import type {
  AuthSessionUser,
  AuthDeviceSession,
  LoginCredentials,
  OAuthCallbackPayload,
  PasswordResetPayload,
  RegisterPayload,
} from './types';

function ensureSuccess<T>(body: ApiEnvelope<T>, fallbackMessage: string): ApiEnvelope<T> {
  if (body.status !== 'Success') {
    throw new ApiError(body.message ?? fallbackMessage, body.statusCode ?? 500, body);
  }
  return body;
}

export async function loginWithPassword(
  credentials: LoginCredentials,
  baseUrl?: string,
): Promise<void> {
  const body = await requestJson<ApiEnvelope<Array<Record<string, unknown>>>>(
    '/auth/login/',
    {
      method: 'POST',
      baseUrl,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...credentials, deviceMetadata: getWebDeviceMetadata() }),
    },
  );
  ensureSuccess(body, 'Login failed');
}

export async function registerAccount(
  payload: RegisterPayload,
  baseUrl?: string,
): Promise<void> {
  const body = await requestJson<ApiEnvelope<unknown>>(
    '/auth/registration/start',
    {
      method: 'POST',
      baseUrl,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  ensureSuccess(body, 'Register failed');
}

export async function resendRegistrationEmail(baseUrl?: string): Promise<void> {
  ensureSuccess(await requestJson<ApiEnvelope<unknown>>('/auth/registration/resend', {
    method: 'POST', baseUrl, headers: { 'Content-Type': 'application/json' }, body: '{}',
  }), 'Failed to resend registration email');
}

export type RegistrationResult = { status: string; email?: string; expiresAt?: number };

export async function completeRegistration(code: string, password: string, baseUrl?: string): Promise<RegistrationResult> {
  const body = ensureSuccess(await requestJson<ApiEnvelope<RegistrationResult>>('/auth/registration/complete', {
    method: 'POST', baseUrl, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code, password }),
  }), 'Registration failed');
  return body.data ?? { status: 'completed' };
}

export async function getRegistrationReceipt(baseUrl?: string): Promise<RegistrationResult> {
  const body = ensureSuccess(await requestJson<ApiEnvelope<RegistrationResult>>('/auth/registration/receipt', { baseUrl }), 'Receipt unavailable');
  return body.data ?? { status: 'not_found' };
}

export async function requestPasswordResetCode(
  email: string,
  baseUrl?: string,
): Promise<void> {
  const body = await requestJson<ApiEnvelope<unknown>>(
    '/auth/password-reset/get-code',
    {
      method: 'POST',
      baseUrl,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    },
  );
  ensureSuccess(body, 'Failed to send password reset code');
}

export async function submitPasswordReset(
  payload: PasswordResetPayload,
  baseUrl?: string,
): Promise<void> {
  const body = await requestJson<ApiEnvelope<unknown>>(
    '/auth/password-reset/verify',
    {
      method: 'POST',
      baseUrl,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  ensureSuccess(body, 'Failed to update password');
}

export async function completeOAuthLogin(
  payload: OAuthCallbackPayload,
  baseUrl?: string,
): Promise<void> {
  const body = await requestJson<ApiEnvelope<Array<Record<string, unknown>>>>(
    '/auth/oauth/callback',
    {
      method: 'POST',
      baseUrl,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, deviceMetadata: getWebDeviceMetadata() }),
    },
  );
  ensureSuccess(body, 'OAuth login failed');
}

export async function fetchCurrentUser(
  baseUrl?: string,
  signal?: AbortSignal,
): Promise<AuthSessionUser> {
  const body = await requestJson<ApiEnvelope<AuthSessionUser>>('/user/me', { baseUrl, signal });
  const success = ensureSuccess(body, 'Failed to load current user');
  if (!success.data || success.data.id == null) {
    throw new ApiError('Current user payload is missing id', 500, success);
  }
  return success.data;
}

export async function logoutCurrentSession(baseUrl?: string): Promise<void> {
  const body = await requestJson<ApiEnvelope<unknown>>('/auth/logout/', {method:'POST',baseUrl});
  ensureSuccess(body, 'Logout failed');
}

export async function fetchDeviceSessions(
  baseUrl?: string,
  signal?: AbortSignal,
): Promise<AuthDeviceSession[]> {
  const body = await requestJson<ApiEnvelope<AuthDeviceSession[]>>('/auth/device-sessions', {
    baseUrl,
    signal,
  });
  const success = ensureSuccess(body, 'Failed to load device sessions');
  return Array.isArray(success.data) ? success.data : [];
}

export async function revokeDeviceSession(
  sessionId: string,
  baseUrl?: string,
): Promise<void> {
  const body = await requestJson<ApiEnvelope<{ revoked: boolean }>>(
    `/auth/device-sessions/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE', baseUrl },
  );
  ensureSuccess(body, 'Failed to revoke device session');
}

export type AuthSecurityEvent = { eventId:string; eventType:string; platform:string|null; createdAt:number; notification:{channel:'email';status:string;sentAt:number|null}|null };
export type TrustedDevice = { deviceId:string; displayName:string|null; status:string; trustExpiresAt:number|null; lastVerifiedAt:number|null };

export async function fetchSecurityEvents(baseUrl?:string):Promise<AuthSecurityEvent[]> {
  const body=await requestJson<ApiEnvelope<AuthSecurityEvent[]>>('/auth/security-events?limit=20',{baseUrl});
  return ensureSuccess(body,'Failed to load security events').data??[];
}
export async function fetchTrustedDevices(baseUrl?:string):Promise<TrustedDevice[]> {
  const body=await requestJson<ApiEnvelope<TrustedDevice[]>>('/auth/trusted-devices',{baseUrl});
  return ensureSuccess(body,'Failed to load trusted devices').data??[];
}
async function stepUp(operation:string,target:Record<string,unknown>,password:string,path:string,baseUrl?:string) {
  const challenge=ensureSuccess(await requestJson<ApiEnvelope<{challengeId:string}>>('/auth/step-up-challenges',{method:'POST',baseUrl,headers:{'Content-Type':'application/json'},body:JSON.stringify({operation,target})}),'Failed to create verification challenge').data;
  if(!challenge) throw new ApiError('Verification challenge missing',500,{});
  return ensureSuccess(await requestJson<ApiEnvelope<unknown>>(path,{method:'POST',baseUrl,headers:{'Content-Type':'application/json'},body:JSON.stringify({challengeId:challenge.challengeId,operation,target,password})}),'Sensitive operation failed');
}
export const revokeOtherSessions=(password:string,baseUrl?:string)=>stepUp('revokeOtherSessions',{scope:'others'},password,'/auth/device-sessions/revoke-others',baseUrl);
export const revokeAllSessions=(password:string,baseUrl?:string)=>stepUp('revokeAllSessions',{scope:'all'},password,'/auth/device-sessions/revoke-all',baseUrl);
